import React, { useState, useEffect } from 'react';
import { Upload, Tag as TagIcon, Loader2, Settings, FileUp, Save, KeyRound, Grid, Eye, Edit, Trash2, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import FigmaPasteArea from '../components/FigmaPasteArea';
import { supabase } from '../lib/supabase';

interface Tag {
  id: string;
  name: string;
  count: number;
}

interface Template {
  id: string;
  name: string;
  thumbnail_url: string;
  created_at: string;
  tags: { name: string }[];
  status: 'active' | 'inactive';
}

type TabType = 'upload' | 'settings' | 'templates' | 'tags';

export default function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState<TabType>('templates');
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [templateType, setTemplateType] = useState<'elementor' | 'figma' | ''>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [jsonContent, setJsonContent] = useState('');
  const [figmaHtmlData, setFigmaHtmlData] = useState('');
  const [loadingTags, setLoadingTags] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  
  // Tag management state
  const [newTagName, setNewTagName] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingTagName, setEditingTagName] = useState('');
  const [showDeleteTagModal, setShowDeleteTagModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<{ id: string; name: string } | null>(null);
  
  const templatesPerPage = 10;

  // Profile settings state
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadTags();
    loadUserProfile();
  }, []);

  useEffect(() => {
    if (activeTab === 'templates') {
      loadUserTemplates(currentPage);
    }
  }, [activeTab, currentPage]);

  const loadUserProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email) {
      setEmail(user.email);
    }
  };

  const loadTags = async () => {
    try {
      setLoadingTags(true);
      setError(null);
      console.log('Fetching tags from Supabase...');

      // Get tags with their template counts
      const { data, error } = await supabase
        .from('tags')
        .select(`
          id,
          name,
          template_tags (
            template_id
          )
        `);

      if (error) {
        console.error('Supabase error:', error);
        setError(`${error.message} (Code: ${error.code})`);
        toast.error('Failed to load tags');
        return;
      }

      // Process the data to count unique templates per tag and sort alphabetically
      const tagsWithCounts = (data || []).map(tag => ({
        id: tag.id,
        name: tag.name,
        count: new Set(tag.template_tags.map((tt: any) => tt.template_id)).size
      })).sort((a, b) => a.name.localeCompare(b.name));

      console.log('Tags with counts:', tagsWithCounts);
      setTags(tagsWithCounts);
    } catch (err) {
      console.error('Error in loadTags:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tags');
      toast.error('Failed to load tags');
    } finally {
      setLoadingTags(false);
    }
  };

  const loadUserTemplates = async (page: number) => {
    setLoadingTemplates(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const from = (page - 1) * templatesPerPage;
      const to = from + templatesPerPage - 1;

      // Get total count
      const { count } = await supabase
        .from('templates')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', user.id);

      setTotalPages(Math.ceil((count || 0) / templatesPerPage));

      // Get templates with tags
      const { data, error } = await supabase
        .from('templates')
        .select(`
          *,
          template_tags (
            tags (
              name
            )
          )
        `)
        .eq('created_by', user.id)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      setTemplates(data.map(template => ({
        ...template,
        tags: template.template_tags.map((tt: any) => tt.tags),
        status: 'active'
      })));
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const validateJsonContent = (content: string): boolean => {
    try {
      const parsed = JSON.parse(content);
      if (!parsed.elements || !Array.isArray(parsed.elements)) {
        toast.error('JSON must contain an elements array');
        return false;
      }
      return true;
    } catch (error) {
      toast.error('Invalid JSON format');
      return false;
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    setTemplateToDelete(templateId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in to delete templates');
      return;
    }

    if (!templateToDelete) return;

    try {
      // First verify the user owns this template
      const { data: template, error: templateError } = await supabase
        .from('templates')
        .select('created_by')
        .eq('id', templateToDelete)
        .single();

      if (templateError) {
        console.error('Error checking template ownership:', templateError);
        toast.error('Failed to verify template ownership');
        return;
      }

      if (template.created_by !== user.id) {
        toast.error('You can only delete your own templates');
        return;
      }

      // Delete the template (trigger will handle file cleanup)
      const { error } = await supabase
        .from('templates')
        .delete()
        .eq('id', templateToDelete);

      if (error) {
        console.error('Error deleting template:', error);
        if (error.code === '42501') {
          toast.error('You do not have permission to delete this template');
        } else if (error.code === '23503') {
          toast.error('Cannot delete template: it is referenced by other records');
        } else {
          toast.error(`Failed to delete template: ${error.message}`);
        }
        return;
      }

      toast.success('Template deleted successfully');
      loadUserTemplates(currentPage);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(error instanceof Error ? error.message : 'An unexpected error occurred while deleting the template');
    }
    setTemplateToDelete(null);
  };

  const handleAddTag = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newTagName.trim()) {
      toast.error('Please enter a tag name');
      return;
    }

    // Check if tag already exists
    const existingTag = tags.find(tag => tag.name.toLowerCase() === newTagName.trim().toLowerCase());
    if (existingTag) {
      toast.error('A tag with this name already exists');
      return;
    }

    setIsAddingTag(true);

    try {
      const { data, error } = await supabase
        .from('tags')
        .insert([{ name: newTagName.trim().toLowerCase() }])
        .select()
        .single();

      if (error) throw error;

      toast.success('Tag added successfully');
      setNewTagName('');
      loadTags(); // Refresh the tags list
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to add tag');
    } finally {
      setIsAddingTag(false);
    }
  };

  const handleDeleteTag = async (tag: { id: string; name: string }) => {
    setTagToDelete(tag);
    setShowDeleteTagModal(true);
  };

  const confirmDeleteTag = async () => {
    if (!tagToDelete) return;

    try {
      // First check if the tag is being used by any templates
      const { data: templateTags, error: checkError } = await supabase
        .from('template_tags')
        .select('template_id')
        .eq('tag_id', tagToDelete.id);

      if (checkError) throw checkError;

      if (templateTags && templateTags.length > 0) {
        toast.error(`Cannot delete tag "${tagToDelete.name}" because it's being used by ${templateTags.length} template(s)`);
        setTagToDelete(null);
        return;
      }

      // Delete the tag
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', tagToDelete.id);

      if (error) throw error;

      toast.success('Tag deleted successfully');
      loadTags(); // Refresh the tags list
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete tag');
    }
    setTagToDelete(null);
  };

  const handleEditClick = (tag: Tag) => {
    setEditingTagId(tag.id);
    setEditingTagName(tag.name);
  };

  const handleCancelEdit = () => {
    setEditingTagId(null);
    setEditingTagName('');
  };

  const handleSaveRename = async () => {
    if (!editingTagId || !editingTagName.trim()) {
      toast.error('Please enter a valid tag name');
      return;
    }

    // Check if the new name already exists (excluding current tag)
    const existingTag = tags.find(tag => 
      tag.name.toLowerCase() === editingTagName.trim().toLowerCase() && 
      tag.id !== editingTagId
    );
    
    if (existingTag) {
      toast.error('A tag with this name already exists');
      return;
    }

    try {
      const { error } = await supabase
        .from('tags')
        .update({ name: editingTagName.trim().toLowerCase() })
        .eq('id', editingTagId);

      if (error) throw error;

      toast.success('Tag renamed successfully');
      setEditingTagId(null);
      setEditingTagName('');
      loadTags(); // Refresh the tags list
    } catch (error) {
      console.error('Error renaming tag:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to rename tag');
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name) {
      toast.error('Please enter a template name');
      return;
    }

    if (!templateType) {
      toast.error('Please select a template type');
      return;
    }

    if (!thumbnail) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (selectedTags.length === 0) {
      toast.error('Select at least 1 tag');
      return;
    }

    // Validate based on template type
    if (templateType === 'elementor') {
      if (jsonContent && !validateJsonContent(jsonContent)) {
        return;
      }
    } else if (templateType === 'figma') {
      if (!figmaHtmlData) {
        toast.error('Please paste Figma component data');
        return;
      }
    }

    setIsLoading(true);

    try {
      console.log('Starting upload process...');
      
      // Upload thumbnail
      const thumbnailPath = `thumbnails/${Date.now()}-${thumbnail.name}`;
      console.log('Uploading thumbnail to:', thumbnailPath);
      const { error: thumbnailError, data: thumbnailData } = await supabase.storage
        .from('thumbnails')
        .upload(thumbnailPath, thumbnail);
      
      if (thumbnailError) {
        console.error('Thumbnail upload error:', thumbnailError);
        throw thumbnailError;
      }
      console.log('Thumbnail uploaded successfully:', thumbnailData);

      let parsedJsonContent = null;

      // Parse JSON content if provided (for Elementor templates)
      if (templateType === 'elementor' && jsonContent) {
        parsedJsonContent = JSON.parse(jsonContent);
      }

      // Get public URLs
      const thumbnailUrl = supabase.storage
        .from('thumbnails')
        .getPublicUrl(thumbnailPath).data.publicUrl;

      console.log('Creating template record...');
      
      // Create template record with appropriate data based on type
      const templateData: any = {
        name,
        thumbnail_url: thumbnailUrl,
        template_type: templateType,
        created_by: (await supabase.auth.getUser()).data.user?.id
      };

      if (templateType === 'elementor') {
        templateData.json_content = parsedJsonContent;
      } else if (templateType === 'figma') {
        templateData.figma_html_data = figmaHtmlData;
      }

      const { data: templateRecord, error: templateInsertError } = await supabase
        .from('templates')
        .insert(templateData)
        .select()
        .single();
      
      if (templateInsertError) {
        console.error('Template record creation error:', templateInsertError);
        throw templateInsertError;
      }
      console.log('Template record created:', templateRecord);

      // Only create template_tags if tags are selected
      if (selectedTags.length > 0) {
        console.log('Creating template tags...');
        const templateTags = selectedTags.map(tagId => ({
          template_id: templateRecord.id,
          tag_id: tagId
        }));

        const { error: tagLinkError } = await supabase
          .from('template_tags')
          .insert(templateTags);
        
        if (tagLinkError) {
          console.error('Template tags creation error:', tagLinkError);
          throw tagLinkError;
        }
        console.log('Template tags created successfully');
      }

      toast.success('Template uploaded successfully');
      setName('');
      setTemplateType('');
      setSelectedTags([]);
      setThumbnailPreview(null);
      setThumbnail(null);
      setJsonContent('');
      setFigmaHtmlData('');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to upload template');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Validate passwords match if updating password
      if (newPassword || confirmPassword) {
        if (newPassword !== confirmPassword) {
          toast.error('New passwords do not match');
          return;
        }
        if (newPassword.length < 8) {
          toast.error('Password must be at least 8 characters long');
          return;
        }
        if (!currentPassword) {
          toast.error('Current password is required to update password');
          return;
        }
      }

      // Verify current password if updating either email or password
      if (currentPassword) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password: currentPassword,
        });

        if (signInError) {
          toast.error('Current password is incorrect');
          return;
        }
      }

      // Update email if changed
      if (email !== (await supabase.auth.getUser()).data.user?.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: email
        });

        if (emailError) throw emailError;
        toast.success('Email updated successfully');
      }

      // Update password if provided
      if (newPassword) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: newPassword
        });

        if (passwordError) throw passwordError;
        toast.success('Password updated successfully');
        setNewPassword('');
        setConfirmPassword('');
      }

      setCurrentPassword('');
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="py-8">
      {/* Tab Navigation */}
      <div className="max-w-2xl mx-auto pt-6 px-4">
        <div className="flex space-x-1 rounded-lg bg-gray-100 p-1">
          <button
            onClick={() => setActiveTab('templates')}
            className={`flex items-center justify-center flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'templates'
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            <Grid className="h-4 w-4 mr-2" />
            My Templates
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex items-center justify-center flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'upload'
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            <FileUp className="h-4 w-4 mr-2" />
            New Template
          </button>
          <button
            onClick={() => setActiveTab('tags')}
            className={`flex items-center justify-center flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'tags'
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            <TagIcon className="h-4 w-4 mr-2" />
            Tags
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center justify-center flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'settings'
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            <Settings className="h-4 w-4 mr-2" />
            Profile
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="py-8">
        <div className="max-w-2xl mx-auto px-4">
          {activeTab === 'templates' ? (
            <div className="bg-white rounded-lg shadow p-6">
              {loadingTemplates ? (
                <div className="flex justify-center items-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center">
                  <Grid className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
                  <p className="text-gray-500 mb-4">Get started by uploading your first template.</p>
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Template
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    {templates.map((template) => (
                      <div key={template.id} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                        <div className="aspect-video relative">
                          <img
                            src={template.thumbnail_url}
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <h3 className="text-sm font-medium text-white mb-1 line-clamp-1">{template.name}</h3>
                            <div className="flex flex-wrap gap-1">
                              {template.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-1.5 py-0.5 text-xs font-medium bg-white/20 text-white rounded-full"
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="p-2 border-t border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-500">
                              {new Date(template.created_at).toLocaleDateString()}
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => window.open(template.thumbnail_url, '_blank')}
                                className="p-1 text-gray-500 hover:text-gray-700"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <Link
                                to={`/admin/templates/${template.id}`}
                                className="p-1 text-gray-500 hover:text-gray-700"
                              >
                                <Edit className="h-4 w-4" />
                              </Link>
                              <button
                                onClick={() => handleDeleteTemplate(template.id)}
                                className="p-1 text-gray-500 hover:text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="mt-6 flex justify-center">
                      <nav className="flex items-center space-x-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                      </nav>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : activeTab === 'tags' ? (
            <div className="bg-white rounded-lg shadow p-6">
              <h1 className="text-2xl font-semibold text-gray-800 mb-6">Tag Management</h1>
              
              {/* Add New Tag Form */}
              <form onSubmit={handleAddTag} className="mb-8">
                <div className="flex items-end space-x-4">
                  <div className="flex-1">
                    <label htmlFor="newTagName" className="block text-sm font-medium text-gray-700 mb-1">
                      Add New Tag
                    </label>
                    <input
                      id="newTagName"
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter tag name (e.g., header, footer, pricing)"
                      disabled={isAddingTag}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isAddingTag || !newTagName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isAddingTag ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Add Tag
                  </button>
                </div>
              </form>

              {/* Tags List */}
              <div>
                <h2 className="text-lg font-medium text-gray-800 mb-4">
                  Existing Tags ({tags.length})
                </h2>
                
                {loadingTags ? (
                  <div className="flex justify-center items-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : error ? (
                  <div className="text-red-600 text-sm mb-4">
                    Error loading tags: {error}
                  </div>
                ) : tags.length === 0 ? (
                  <div className="text-center py-8">
                    <TagIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No tags yet</h3>
                    <p className="text-gray-500">Create your first tag to start organizing templates.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {tags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center justify-between px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center flex-1">
                          <TagIcon className="h-4 w-4 text-gray-500 mr-3 flex-shrink-0" />
                          {editingTagId === tag.id ? (
                            <input
                              type="text"
                              value={editingTagName}
                              onChange={(e) => setEditingTagName(e.target.value)}
                              className="flex-1 px-2 py-1 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveRename();
                                } else if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <div className="flex items-center justify-between flex-1">
                              <span className="text-sm font-medium text-gray-800 capitalize">
                                {tag.name}
                              </span>
                              <span className="text-xs text-gray-500 ml-3">
                                {tag.count} {tag.count === 1 ? 'template' : 'templates'}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-1 ml-3">
                          {editingTagId === tag.id ? (
                            <>
                              <button
                                onClick={handleSaveRename}
                                className="p-1 text-green-600 hover:text-green-700 transition-colors"
                                title="Save changes"
                              >
                                <Save className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Cancel editing"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditClick(tag)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title={`Rename ${tag.name} tag`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteTag(tag)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                title={`Delete ${tag.name} tag`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Usage Guidelines */}
              <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 mb-2">Tag Guidelines</h3>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Use lowercase, descriptive names (e.g., "header", "pricing", "testimonials")</li>
                  <li>• Keep tag names short and consistent</li>
                  <li>• Tags in use by templates cannot be deleted</li>
                  <li>• Consider creating tags for: layout types, content sections, and design styles</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              {activeTab === 'upload' ? (
                <>
                  <h1 className="text-2xl font-semibold text-gray-800 mb-6">Upload Template</h1>
                  <form onSubmit={handleUploadSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="templateName" className="block text-sm font-medium text-gray-700 mb-1">
                        Template Name *
                      </label>
                      <input
                        id="templateName"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Tags *
                      </label>
                      {error ? (
                        <div className="text-red-600 text-sm mb-2">
                          Error loading tags: {error}
                        </div>
                      ) : loadingTags ? (
                        <div className="flex items-center text-sm text-gray-500">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Loading tags...
                        </div>
                      ) : tags.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {tags.map(tag => (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => toggleTag(tag.id)}
                              className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                selectedTags.includes(tag.id)
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                              }`}
                            >
                              <TagIcon className="h-4 w-4 mr-1.5" />
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No tags available</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="thumbnail" className="block text-sm font-medium text-gray-700 mb-1">
                        Thumbnail Image *
                      </label>
                      <div className="space-y-2">
                        {thumbnailPreview && (
                          <div className="relative inline-block">
                            <img
                              src={thumbnailPreview}
                              alt="Thumbnail preview"
                              className="h-24 w-auto object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setThumbnail(null);
                                setThumbnailPreview(null);
                                // Reset the file input
                                const fileInput = document.getElementById('thumbnail') as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              }}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        <input
                          id="thumbnail"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setThumbnail(file);
                              const url = URL.createObjectURL(file);
                              setThumbnailPreview(url);
                            } else {
                              setThumbnail(null);
                              setThumbnailPreview(null);
                            }
                          }}
                          className="sr-only"
                          required
                        />
                        <div className="flex items-center">
                          <label
                            htmlFor="thumbnail"
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                          >
                            {thumbnailPreview ? 'Change image' : 'Choose image'}
                          </label>
                          {thumbnailPreview && (
                            <span className="text-sm text-gray-500 ml-3">
                              {thumbnail?.name || 'Image selected'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Template Type *
                      </label>
                      <select
                        value={templateType}
                        onChange={(e) => setTemplateType(e.target.value as 'elementor' | 'figma' | '')}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select template type...</option>
                        <option value="elementor">Elementor</option>
                        <option value="figma">Figma</option>
                      </select>
                    </div>

                    {templateType === 'elementor' && (
                      <div>
                        <label htmlFor="jsonContent" className="block text-sm font-medium text-gray-700 mb-1">
                          Paste Elementor JSON here
                        </label>
                        <textarea
                          id="jsonContent"
                          value={jsonContent}
                          onChange={(e) => setJsonContent(e.target.value)}
                          className="w-full h-32 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Paste your Elementor JSON content here..."
                        />
                      </div>
                    )}

                    {templateType === 'figma' && (
                      <FigmaPasteArea
                        onHtmlDataCapture={setFigmaHtmlData}
                        htmlData={figmaHtmlData}
                      />
                    )}

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Uploading...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <Upload className="h-5 w-5 mr-2" />
                          Upload Template
                        </span>
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-semibold text-gray-800 mb-6">Profile Settings</h1>
                  <form onSubmit={handleProfileSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                          Current Password
                        </label>
                        <input
                          id="currentPassword"
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <input
                          id="newPassword"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Leave blank to keep current password"
                        />
                      </div>

                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm New Password
                        </label>
                        <input
                          id="confirmPassword"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSaving}
                      className="w-full bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <span className="flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <Save className="h-5 w-5 mr-2" />
                          Save Changes
                        </span>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      <ConfirmationModal
        isOpen={showDeleteTagModal}
        onClose={() => setShowDeleteTagModal(false)}
        onConfirm={confirmDeleteTag}
        title="Delete Tag"
        message={`Are you sure you want to delete the tag "${tagToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}